import React, {useMemo, useState} from 'react';
import {designSystemToken} from '@lightricks/react-design-system';
import LEGACY_COLORS from '@/config/legacyColors';
import styles from './SocialNetworkIcon.module.scss';

export type SocialNetworkIconProps = {
  testID?: string;
  name?: string | null;
  width?: number | string;
  height?: number | string;
  color?: string;
  withHoverMode?: boolean;
};

type IconProps = {
  width?: number | string;
  height?: number | string;
  color?: string;
};

// The YouTube icon may not be recolored, so it accepts no `color` at all.
type UncolorableIconProps = Omit<IconProps, 'color'>;

function NoNetwork(props: IconProps) {
  const {
    width,
    height,
    color = designSystemToken('semantic.bg.neutral'),
  } = props;
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width={width || 18}
      height={height || 18}
      viewBox="0 0 18 18"
    >
      <title>Icons/Content-Only</title>
      <path
        fill={color || '#404040'}
        fillRule="nonzero"
        d="M7.691 3a5.229 5.229 0 0 0-.724 1.5H1.5v12h12v-5.467a5.229 5.229 0 0 0 1.5-.724V16.5a1.5 1.5 0 0 1-1.5 1.5h-12A1.5 1.5 0 0 1 0 16.5v-12A1.5 1.5 0 0 1 1.5 3h6.191Zm3.832 3.477L8.25 5.25l3.273-1.227L12.75.75l1.227 3.273L17.25 5.25l-3.273 1.227L12.75 9.75l-1.227-3.273Zm-4.569 4.569L4.5 10.125l2.454-.92.921-2.455.92 2.454 2.455.921-2.454.92-.921 2.455-.92-2.454Z"
      />
    </svg>
  );
}

function Facebook(props: IconProps) {
  const {
    width,
    height,
    color = designSystemToken('semantic.bg.neutral'),
  } = props;
  return (
    <svg
      width={width || 25}
      height={height || 24}
      viewBox="0 0 25 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M20.9284 12.0503C20.9284 7.22929 17.0446 3.32141 12.2532 3.32141C7.4618 3.32141 3.57129 7.22929 3.57129 12.0503C3.57129 16.4148 6.74776 20.0272 10.8919 20.6786V14.575H8.68968V12.0503H10.8919V10.1233C10.8919 7.93432 12.1865 6.72569 14.1684 6.72569C15.116 6.72569 16.1103 6.89356 16.1103 6.89356V9.04222H15.0159C13.9415 9.04222 13.6012 9.71367 13.6012 10.4053V12.0503H16.0102L15.6232 14.575H13.6012V20.6786C17.752 20.0272 20.9284 16.4148 20.9284 12.0503Z"
        fill={color}
      />
    </svg>
  );
}

function Instagram(props: IconProps) {
  const {
    width,
    height,
    color = designSystemToken('semantic.bg.neutral'),
  } = props;

  return (
    <svg
      width={width || 25}
      height={height || 24}
      viewBox="0 0 25 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M12.2496 4.79715C14.5955 4.79715 14.8733 4.80608 15.7997 4.84835C16.6563 4.88745 17.1215 5.03057 17.4311 5.15086C17.8412 5.31024 18.1339 5.50065 18.4413 5.80807C18.7488 6.1155 18.9392 6.4082 19.0985 6.81826C19.2188 7.12788 19.362 7.59308 19.4011 8.44964C19.4433 9.3761 19.4523 9.65394 19.4523 11.9998C19.4523 14.3457 19.4433 14.6236 19.4011 15.55C19.362 16.4066 19.2188 16.8718 19.0985 17.1814C18.9392 17.5915 18.7488 17.8842 18.4413 18.1916C18.1339 18.499 17.8412 18.6894 17.4311 18.8488C17.1215 18.9691 16.6563 19.1122 15.7997 19.1513C14.8734 19.1936 14.5956 19.2025 12.2496 19.2025C9.90353 19.2025 9.62568 19.1936 8.6994 19.1513C7.8428 19.1122 7.3776 18.9691 7.06802 18.8488C6.65792 18.6894 6.36522 18.499 6.0578 18.1916C5.75037 17.8842 5.55996 17.5915 5.40061 17.1814C5.28029 16.8718 5.13717 16.4066 5.09807 15.55C5.0558 14.6236 5.04688 14.3457 5.04688 11.9998C5.04688 9.65394 5.0558 9.3761 5.09807 8.44968C5.13717 7.59308 5.28029 7.12788 5.40061 6.81826C5.55996 6.4082 5.75037 6.1155 6.0578 5.80807C6.36522 5.50065 6.65792 5.31024 7.06802 5.15086C7.3776 5.03057 7.8428 4.88745 8.69936 4.84835C9.62582 4.80608 9.90367 4.79715 12.2496 4.79715ZM12.2496 3.21411C9.86349 3.21411 9.56433 3.22423 8.62724 3.26698C7.69207 3.30967 7.05344 3.45817 6.49457 3.67537C5.91683 3.89989 5.42687 4.2003 4.93845 4.68873C4.45003 5.17715 4.14961 5.66711 3.92509 6.24485C3.70789 6.80372 3.55939 7.44235 3.5167 8.37752C3.47395 9.31458 3.46387 9.61377 3.46387 11.9998C3.46387 14.3859 3.47395 14.6851 3.5167 15.6222C3.55939 16.5573 3.70789 17.196 3.92509 17.7548C4.14961 18.3325 4.45003 18.8225 4.93845 19.311C5.42687 19.7994 5.91683 20.0998 6.49457 20.3243C7.05344 20.5415 7.69207 20.69 8.62724 20.7327C9.56433 20.7755 9.86349 20.7855 12.2496 20.7855C14.6356 20.7855 14.9348 20.7755 15.8719 20.7327C16.8071 20.69 17.4457 20.5415 18.0046 20.3243C18.5823 20.0998 19.0723 19.7994 19.5607 19.311C20.0491 18.8225 20.3495 18.3326 20.574 17.7548C20.7912 17.196 20.9397 16.5573 20.9824 15.6222C21.0252 14.6851 21.0353 14.3859 21.0353 11.9998C21.0353 9.61377 21.0252 9.31458 20.9824 8.37752C20.9397 7.44235 20.7912 6.80372 20.574 6.24485C20.3495 5.66711 20.0491 5.17715 19.5607 4.68873C19.0723 4.2003 18.5823 3.89989 18.0046 3.67537C17.4457 3.45817 16.8071 3.30967 15.8719 3.26698C14.9348 3.22423 14.6356 3.21411 12.2496 3.21411ZM12.2496 7.48825C9.75789 7.48825 7.73797 9.50817 7.73797 11.9998C7.73797 14.4915 9.75789 16.5114 12.2496 16.5114C14.7412 16.5114 16.7612 14.4915 16.7612 11.9998C16.7612 9.50817 14.7412 7.48825 12.2496 7.48825ZM12.2496 14.9284C10.6322 14.9284 9.32097 13.6172 9.32097 11.9998C9.32097 10.3824 10.6322 9.07125 12.2496 9.07125C13.867 9.07125 15.1782 10.3824 15.1782 11.9998C15.1782 13.6172 13.867 14.9284 12.2496 14.9284ZM17.9937 7.31C17.9937 7.89227 17.5217 8.3643 16.9394 8.3643C16.3571 8.3643 15.8851 7.89227 15.8851 7.31C15.8851 6.72773 16.3571 6.25573 16.9394 6.25573C17.5217 6.25573 17.9937 6.72773 17.9937 7.31Z"
        fill={color}
      />
    </svg>
  );
}

function Pinterest(props: IconProps) {
  const {
    width,
    height,
    color = designSystemToken('semantic.bg.neutral'),
  } = props;

  return (
    <svg
      width={width || 25}
      height={height || 24}
      viewBox="0 0 25 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M12.2505 3.32141C7.45722 3.32141 3.57129 7.20695 3.57129 11.9992C3.57129 15.5537 5.70788 18.6065 8.76641 19.9486C8.74052 19.3421 8.76075 18.6154 8.91609 17.9563C9.08386 17.2513 10.034 13.2265 10.034 13.2265C10.034 13.2265 9.75704 12.6724 9.75704 11.8539C9.75704 10.5675 10.5023 9.60724 11.4298 9.60724C12.2201 9.60724 12.6005 10.2005 12.6005 10.91C12.6005 11.7031 12.0951 12.8908 11.8351 13.9898C11.6178 14.9106 12.2966 15.6618 13.2039 15.6618C14.8486 15.6618 15.9563 13.549 15.9563 11.0473C15.9563 9.14569 14.6752 7.72191 12.345 7.72191C9.71197 7.72191 8.07295 9.68492 8.07295 11.8776C8.07295 12.6341 8.29474 13.1668 8.64483 13.5794C8.80473 13.7696 8.82722 13.8462 8.76869 14.0635C8.72702 14.2234 8.63244 14.6078 8.59193 14.7597C8.53444 14.9799 8.35663 15.0581 8.15741 14.9776C6.94507 14.4817 6.38106 13.155 6.38106 11.662C6.38106 9.19632 8.46023 6.2407 12.5825 6.2407C15.8965 6.2407 18.0782 8.63771 18.0782 11.2117C18.0782 14.6162 16.1848 17.1601 13.3942 17.1601C12.4576 17.1601 11.5761 16.6529 11.2744 16.0777C11.2744 16.0777 10.7702 18.0779 10.6644 18.4634C10.4798 19.1321 10.1195 19.8016 9.79076 20.3229C10.5709 20.5531 11.396 20.6786 12.2505 20.6786C17.0437 20.6786 20.9284 16.7931 20.9284 11.9991C20.9284 7.20695 17.0437 3.32141 12.2505 3.32141Z"
        fill={color || 'black'}
      />
    </svg>
  );
}

function Tiktok(props: IconProps) {
  const {
    width,
    height,
    color = designSystemToken('semantic.bg.neutral'),
  } = props;

  return (
    <svg
      width={width || 25}
      height={height || 24}
      viewBox="0 0 25 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M15.7654 3.42859C16.0622 5.86124 17.4872 7.31157 19.9647 7.46586V10.202C18.5289 10.3357 17.2713 9.88823 15.8085 9.04477V14.1621C15.8085 20.6629 8.37064 22.6944 5.38036 18.0348C3.45882 15.0364 4.6355 9.77508 10.7996 9.56422V12.4495C10.33 12.5215 9.82799 12.6346 9.3692 12.7837C7.99821 13.226 7.22095 14.0541 7.43685 15.5147C7.85247 18.3125 13.2393 19.1405 12.7913 13.6735V3.43373H15.7654V3.42859Z"
        fill={color}
      />
    </svg>
  );
}

function Twitter(props: IconProps) {
  const {
    width,
    height,
    color = designSystemToken('semantic.bg.neutral'),
  } = props;

  return (
    <svg
      width={width || 24}
      height={height || 24}
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M2.78882 3.32895L15.5069 20.6449H21.2113L8.62416 3.32895H2.78882ZM5.66699 4.78609L16.2446 19.1878H18.3506L7.88193 4.78609H5.66699Z"
        fill={color}
      />
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M9.65592 12.6787L3.12971 20.6711H5.95862L10.9854 14.4889L9.65592 12.6787ZM14.0218 10.7545L20.0596 3.32895H17.2905L12.7057 8.94384L14.0218 10.7545Z"
        fill={color}
      />
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M3.12971 20.6711H5.95862L10.9854 14.4889L9.65592 12.6787L3.12971 20.6711ZM20.0596 3.32895H17.2905L12.7057 8.94384L14.0218 10.7545L20.0596 3.32895Z"
        fill={color}
      />
    </svg>
  );
}

// Deliberately ignores the caller's `color`: YouTube's Developer Policies (III.F.2a)
// forbid modifying the colors of the YouTube icon, so it always renders in YouTube's
// own almost-black — never dimmed for an unselected state or tinted on hover.
function Youtube(props: UncolorableIconProps) {
  const {width, height} = props;

  return (
    <svg
      version="1.0"
      xmlns="http://www.w3.org/2000/svg"
      width={width || 24}
      height={height || 24}
      viewBox="0 0 64.000000 45.000000"
      preserveAspectRatio="xMidYMid meet"
    >
      <g
        transform="translate(0.000000,45.000000) scale(0.100000,-0.100000)"
        fill={LEGACY_COLORS.ALMOST_BLACK}
        stroke="none"
      >
        <path
          d="M123 442 c-43 -5 -69 -13 -83 -28 -29 -28 -43 -135 -35 -253 6 -85
  10 -97 34 -122 25 -25 36 -27 137 -34 60 -4 172 -5 248 -2 205 10 210 15 211
  222 0 150 -8 180 -56 205 -24 14 -362 22 -456 12z m288 -220 c-4 -7 -149 -92
  -157 -92 -6 0 -5 176 1 182 8 8 161 -81 156 -90z"
        />
      </g>
    </svg>
  );
}

function Google(props: IconProps) {
  const {
    width,
    height,
    color = designSystemToken('semantic.bg.neutral'),
  } = props;
  return (
    <svg
      width={width || 25}
      height={height || 24}
      viewBox="0 0 25 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M12.5255 13.5775V10.4886H20.2976C20.4139 11.0117 20.5052 11.5016 20.5052 12.1908C20.5052 16.9322 17.325 20.3034 12.5338 20.3034C7.95023 20.3034 4.23022 16.5834 4.23022 11.9999C4.23022 7.41629 7.95023 3.69629 12.5338 3.69629C14.7758 3.69629 16.6524 4.51834 18.0889 5.86352L15.7307 8.15531C15.1328 7.59067 14.0866 6.92638 12.5338 6.92638C9.78532 6.92638 7.54335 9.20986 7.54335 12.0082C7.54335 14.8065 9.78532 17.09 12.5338 17.09C15.7141 17.09 16.8849 14.8895 17.1008 13.5858H12.5255V13.5775Z"
        fill={color}
      />
    </svg>
  );
}

const SOCIAL_ICON_MAP = {
  'facebook': Facebook,
  'instagram': Instagram,
  'pinterest': Pinterest,
  'tiktok': Tiktok,
  'tiktok-creator-marketplace': Tiktok,
  'tiktok_creator_marketplace': Tiktok,
  'tiktok-v2': Tiktok,
  'tiktok_v2': Tiktok,
  'twitter': Twitter,
  'youtube': Youtube,
  'google': Google,
} as {[key: string]: React.FC<IconProps>};

function SocialNetworkIcon(props: SocialNetworkIconProps) {
  const {
    testID = 'social-network-icon',
    name,
    width,
    height,
    color,
    withHoverMode,
  } = props;

  const Icon = name ? SOCIAL_ICON_MAP[name] || NoNetwork : NoNetwork;
  const [isHovered, setIsHovered] = useState(false);

  const hoverProps = useMemo(() => {
    if (withHoverMode) {
      return {
        onMouseEnter: () => setIsHovered(true),
        onMouseLeave: () => setIsHovered(false),
      };
    }
    return {};
  }, [withHoverMode]);

  return (
    <div className={styles.container} data-testid={testID} {...hoverProps}>
      <Icon
        width={width}
        height={height}
        color={isHovered && withHoverMode ? 'rgba(163, 166, 172, 1)' : color}
      />
    </div>
  );
}

export default SocialNetworkIcon;
